import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatDubaiDate } from '../shared/format';

/**
 * Module 2 — Creator onboarding row.
 * Compact row used inside a bucket. On click → expands a detail sheet.
 */
const STATUS_COLOR = {
  Passed: 'green',
  'On track': 'cyan',
  'At risk': 'amber',
  Missed: 'red',
  'Not started': 'cyan',
  Graduated: 'diamond',
};

const StatusChip = ({ kind, status, t }) => {
  if (!status) return null;
  return (
    <Chip color={STATUS_COLOR[status] || 'cyan'} size="sm">
      {`${kind}: ${t(`tcn.m2.status.${status.replace(/\s+/g, '_').toLowerCase()}`, status)}`}
    </Chip>
  );
};

const CreatorOnboardingRow = ({ creator, onClick, expanded = false }) => {
  const { t } = useTranslation();
  if (!creator) return null;
  const handle = creator.tiktok_username
    ? `@${creator.tiktok_username}`
    : creator.display_name || creator.tiktok_id || '—';

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderBottom: `1px solid ${TCN.border}`,
        cursor: 'pointer',
        background: expanded ? TCN.surface2 : 'transparent',
      }}
    >
      <Avatar name={creator.display_name || handle} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: TCN.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {handle}
          </span>
          <span
            style={{
              fontSize: 11,
              color: TCN.textDim,
              fontFamily: TCN.fontMono,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {t('tcn.m2.daysSince', 'D{{n}}', { n: creator.days_since_joining ?? '?' })}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {creator.t14_status && <StatusChip kind="T14" status={creator.t14_status} t={t} />}
          {creator.t30_status && <StatusChip kind="T30" status={creator.t30_status} t={t} />}
          {creator.graduation_status && (
            <StatusChip kind="Grad" status={creator.graduation_status} t={t} />
          )}
        </div>
        {creator.next_milestone_date && (
          <div
            style={{
              fontSize: 11,
              color: TCN.textMute,
              marginTop: 4,
            }}
          >
            {t('tcn.m2.nextMilestone', 'Next milestone: {{date}}', {
              date: formatDubaiDate(creator.next_milestone_date),
            })}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, color: TCN.textMute, display: 'flex', alignItems: 'center' }}>
        <Icon name={expanded ? 'chevron-down' : 'chevron'} size={16} />
      </div>
    </div>
  );
};

export default CreatorOnboardingRow;
